/**
 * Words and number formats for the Availability report. Hours only: a month is
 * Booked against Available Hours, never "capacity" (points).
 */
import type { CellState } from "@/lib/availability/cell-state";
import { parseDay } from "@/lib/availability/months";

export const STATE_LABEL: Record<CellState, string> = {
  overUtilized: "Over-utilized",
  stretched: "Stretched",
  fullyBooked: "Fully booked",
  someRoom: "Some room",
  available: "Available",
  unplanned: "Unplanned",
  noData: "No data",
};

const LONG_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** `"2026-11-01"` → `"November 2026"`. */
export function fullMonthLabel(month: string): string {
  const { year, month: index } = parseDay(month);
  return `${LONG_MONTH_NAMES[index - 1] ?? month} ${year}`;
}

/** A whole number of hours with thousands separators: `1620` → `"1,620"`. */
export function formatHours(hours: number): string {
  return Math.round(hours).toLocaleString("en-US");
}

/**
 * A net in the grid's own convention: `"+111"` for hours OVER, `"63"` for hours
 * FREE, `"0"` for none. The contract's `netHours` is Available − Booked, so a
 * negative value is hours over.
 */
export function formatNet(netHours: number): string {
  const hours = Math.round(Math.abs(netHours));
  if (hours === 0) return "0";
  return netHours < 0 ? `+${formatHours(hours)}` : formatHours(hours);
}

/** The same net in words, for a panel or tooltip: `"+111 h over"` / `"63 h free"`. */
export function formatNetWords(netHours: number): string {
  const hours = Math.round(Math.abs(netHours));
  if (hours === 0) return "0 h";
  return netHours < 0 ? `${formatHours(hours)} h over` : `${formatHours(hours)} h free`;
}

/** `"2026-10-16T…"`-style ISO → `"31 Oct, 13:04"` in the viewer's time zone. */
export function formatDataAsOf(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${day}, ${time}`;
}

/** What a Pod position means, for a tooltip — the code stays canonical, the words explain it. */
export const POD_POSITION_HINT: Record<string, string> = {
  R1: "R1 — Primary, named delivery owner.",
  R2: "R2 — Primary, second named delivery Resource.",
  R3: "R3 — Primary, third named delivery Resource.",
  R4: "R4 — Primary, fourth named delivery Resource.",
  Supporting: "Supporting — helps on the Solution but is not a Primary.",
};

/** Why a cell carries the amber corner, in plain words. */
export const ISSUE_TEXT: Record<"timeOffUnavailable" | "unmappedGuidance", string> = {
  timeOffUnavailable:
    "NOS time off could not be read for this month, so Available Hours assume no leave.",
  unmappedGuidance:
    "Some Guidance sits on a Demand with no Solution link, so it is not counted in Booked.",
};
